// PT-AO + EN string tables. Mirrors the shape of
// `kwanza.digests.render._STRINGS` on the backend so the parity contract
// is identical across surfaces: every key MUST exist in every supported
// language. The Vitest parity test pins this; adding a key in one
// language without the other fails the build.
//
// PT-AO is the default for the Lusophone primary market; EN is the
// fallback for portfolio + Customer-#N onboard cases. Adding a third
// language = one more column in every _STRINGS entry — no per-component
// code change.
//
// IMPORTANT: per spec §4 (locked constraint 7) — no PT-AO-only or EN-only
// renderers. The parity assertion in tests/unit/i18n.test.ts is the gate.

export const SUPPORTED_LANGUAGES = ["pt-AO", "en"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

const DEFAULT_LANGUAGE: Language = "pt-AO";

// Translation table. Each key is a stable identifier consumed at the
// component layer; the value is a per-language string. Two-form values
// (singular / plural) live under sibling keys (`*.one` / `*.other`).
const STRINGS = {
  "app.title": {
    "pt-AO": "Kwanza",
    "en": "Kwanza",
  },
  "app.subtitle": {
    "pt-AO": "Painel do agente de contabilidade",
    "en": "Accounting agent dashboard",
  },
  // Human-readable window label for the header + KPI aria-label. The
  // backend's `period_label` is a machine token (`P30D`) and must NOT be
  // rendered raw; the dashboard window is fixed at 30 days (no selector
  // yet), so this is a static string — same shape as `sovereignty.col.count`.
  "app.period": {
    "pt-AO": "Últimos 30 dias",
    "en": "Last 30 days",
  },
  "loading": {
    "pt-AO": "A carregar…",
    "en": "Loading…",
  },
  "error.session_expired.title": {
    "pt-AO": "Sessão expirada",
    "en": "Session expired",
  },
  "error.session_expired.body": {
    "pt-AO":
      "A sessão de autenticação do Teams terminou. Volte a abrir o separador para continuar.",
    "en":
      "The Teams authentication session has ended. Reopen the tab to continue.",
  },
  "error.session_expired.action": {
    "pt-AO": "Reabrir sessão",
    "en": "Re-open session",
  },
  "error.tenant_unknown.title": {
    "pt-AO": "Organização não registada",
    "en": "Organisation not registered",
  },
  // Tenant-neutral on purpose: the SPA is a single shared artifact served
  // to every tenant from Cloudflare Pages, and this message renders for a
  // tenant that is BY DEFINITION not yet registered — there is no per-tenant
  // config to pull a provider name from. Refer to the universal product
  // brand ("Kwanza"), never a specific operator/reseller, so the same bundle
  // is correct under we-resell, customer-owned, and hybrid topologies.
  // "Organização" (not the literal "inquilino"/tenant) — the persona reads
  // "inquilino" as a lodger, not their M365 environment (F37 de-jargon pass).
  "error.tenant_unknown.body": {
    "pt-AO":
      "A sua organização ainda não está registada no Kwanza. Contacte o suporte do Kwanza.",
    "en":
      "Your organisation isn't registered with Kwanza yet. Contact Kwanza support.",
  },
  "error.fetch_failed.title": {
    "pt-AO": "Não foi possível carregar os dados",
    "en": "Couldn't load data",
  },
  "error.fetch_failed.body": {
    "pt-AO":
      "Ocorreu um erro ao falar com o serviço Kwanza. Tente novamente em alguns segundos.",
    "en":
      "Something went wrong talking to the Kwanza service. Try again in a few seconds.",
  },
  "error.retry": {
    "pt-AO": "Tentar novamente",
    "en": "Retry",
  },
  "error.detail_label": {
    "pt-AO": "Detalhe técnico",
    "en": "Technical detail",
  },
  "kpi.invoices_processed": {
    "pt-AO": "Facturas processadas",
    "en": "Invoices processed",
  },
  "kpi.invoices_filed": {
    "pt-AO": "Facturas arquivadas",
    "en": "Invoices filed",
  },
  "kpi.hours_saved": {
    "pt-AO": "Horas poupadas",
    "en": "Hours saved",
  },
  // "Hours saved" is a derived estimate (invoices × per-invoice handling
  // time), not a measured value — marked as such per F37 §12.3 so the
  // customer never reads it as audited fact.
  "kpi.hours_saved_note": {
    "pt-AO": "estimativa",
    "en": "estimate",
  },
  // Counts items the agent flagged for a human look (low-confidence
  // classifications, blocked sends, etc.). Renamed from "Excepções" (read
  // as "errors") per F37 §12.3 #3 — these are review items, not failures.
  // Source is still customer_view.exceptions_raised_count (relabel only).
  "kpi.items_to_review": {
    "pt-AO": "Itens a rever",
    "en": "Items to review",
  },
  "kpi.new_vendors": {
    "pt-AO": "Novos fornecedores",
    "en": "New vendors",
  },
  "kpi.period_label": {
    "pt-AO": "Janela",
    "en": "Window",
  },
  // Muted secondary line under the 3 headline tiles (Task 4) — the softer /
  // estimated figures, de-emphasised. {h} hours, {n} vendor count.
  "kpi.secondary.hours": {
    "pt-AO": "~{h}h poupadas (estimativa)",
    "en": "~{h}h saved (estimate)",
  },
  "kpi.secondary.vendors.one": {
    "pt-AO": "{n} novo fornecedor descoberto",
    "en": "{n} new vendor discovered",
  },
  "kpi.secondary.vendors.other": {
    "pt-AO": "{n} novos fornecedores descobertos",
    "en": "{n} new vendors discovered",
  },
  "kpi.empty": {
    "pt-AO": "Sem dados para esta janela.",
    "en": "No data for this window.",
  },
  "activity.heading": {
    "pt-AO": "Actividade recente",
    "en": "Recent activity",
  },
  "activity.empty": {
    "pt-AO": "Sem actividade registada.",
    "en": "No recent activity.",
  },
  // The teaser is count-based ("what just happened") and deliberately
  // ignores the period picker at the top of the page — so it says so. The
  // 2026-07-28 design review (finding 7) caught the screen showing one
  // global control and four cards, two of which quietly opted out of it.
  "activity.teaser_scope": {
    "pt-AO": "Últimos 5 eventos, seja de que período for",
    "en": "Latest 5 events, whatever the period",
  },
  // Overview teaser → Atividade tab link. The teaser shows the last 5
  // events (both outcomes); this links out to the full filterable surface.
  "activity.see_all": {
    "pt-AO": "Ver toda a actividade →",
    "en": "View all activity →",
  },
  // Drill-down reference id (formerly "Correlação" — jargon the persona
  // doesn't recognise). Rendered as a copy-on-click chip; clicking copies
  // the full id. Per F37 §12.3/§12.4 (relabel + copy-on-click + de-noise).
  "reference.label": {
    "pt-AO": "Referência",
    "en": "Reference",
  },
  // Clean action verb only — NOT the id. The accessible name reads "Copy
  // reference"; the (truncated) id stays visible button text for sighted
  // users. Announcing the truncated id would read out an opaque fragment
  // that isn't even what gets copied (the FULL value is). Per F37 §12.4.
  "reference.copy": {
    "pt-AO": "Copiar referência",
    "en": "Copy reference",
  },
  "reference.copied": {
    "pt-AO": "Referência copiada",
    "en": "Reference copied",
  },
  "search.heading": {
    "pt-AO": "Procurar factura",
    "en": "Find an invoice",
  },
  "search.hint": {
    "pt-AO": "Cole o identificador de correlação de uma factura para ver o que o agente fez.",
    "en": "Paste an invoice's correlation id to see what the agent did.",
  },
  "search.placeholder": {
    "pt-AO": "Identificador de correlação",
    "en": "Correlation id",
  },
  "search.submit": {
    "pt-AO": "Procurar",
    "en": "Search",
  },
  "search.loading": {
    "pt-AO": "A procurar…",
    "en": "Searching…",
  },
  "search.not_found.title": {
    "pt-AO": "Factura não encontrada",
    "en": "Invoice not found",
  },
  "search.not_found.body": {
    "pt-AO":
      "Nenhuma factura corresponde a esse identificador. Verifique o valor e tente novamente.",
    "en":
      "No invoice matches that id. Check the value and try again.",
  },
  "search.error.title": {
    "pt-AO": "Não foi possível procurar",
    "en": "Couldn't run the search",
  },
  "search.error.body": {
    "pt-AO":
      "Ocorreu um erro ao falar com o serviço Kwanza. Tente novamente em alguns segundos.",
    "en":
      "Something went wrong talking to the Kwanza service. Try again in a few seconds.",
  },
  // --- F37 §12.6 browsable invoice list (Task 5) ------------------------
  "invoices.list.heading": {
    "pt-AO": "Facturas",
    "en": "Invoices",
  },
  "invoices.filter_label": {
    "pt-AO": "Filtrar por estado",
    "en": "Filter by status",
  },
  "invoices.filter.all": {
    "pt-AO": "Todas",
    "en": "All",
  },
  "invoices.filter.filed": {
    "pt-AO": "Arquivadas",
    "en": "Filed",
  },
  "invoices.filter.review": {
    "pt-AO": "A rever",
    "en": "To review",
  },
  "invoices.status.filed": {
    "pt-AO": "Arquivada",
    "en": "Filed",
  },
  "invoices.status.review": {
    "pt-AO": "A rever",
    "en": "To review",
  },
  "invoices.search_label": {
    "pt-AO": "Procurar facturas",
    "en": "Search invoices",
  },
  // The text box filters the rows already fetched — it is NOT a server-side
  // search. The old placeholder ("procurar por fornecedor ou número")
  // promised the whole window; load-more then re-search and the same query
  // returned more results than a second earlier (design review finding 8).
  "invoices.search_placeholder": {
    "pt-AO": "Filtrar as carregadas por fornecedor ou número…",
    "en": "Filter the loaded rows by vendor or number…",
  },
  "invoices.empty": {
    "pt-AO": "Nenhuma factura corresponde aos filtros.",
    "en": "No invoices match the filters.",
  },
  // Counts the LOADED page, not the window — say so rather than reading as
  // an authoritative total (design review finding 8). `count_more` appends
  // when the backend still has a cursor.
  "invoices.count_loaded": {
    "pt-AO": "{n} carregadas",
    "en": "{n} loaded",
  },
  "invoices.count_more": {
    "pt-AO": "+ mais",
    "en": "+ more",
  },
  "invoices.back": {
    "pt-AO": "Voltar à lista",
    "en": "Back to the list",
  },
  // Power-user disclosure at the bottom of the list — the demoted
  // correlation-id search (the old primary entry point).
  "invoices.ref_disclosure": {
    "pt-AO":
      "Tem o identificador exacto de uma factura? Procurar por referência",
    "en": "Have an invoice's exact id? Search by reference",
  },

  "invoice.vendor": {
    "pt-AO": "Fornecedor",
    "en": "Vendor",
  },
  "invoice.invoice_number": {
    "pt-AO": "Número da factura",
    "en": "Invoice number",
  },
  "invoice.invoice_date": {
    "pt-AO": "Data da factura",
    "en": "Invoice date",
  },
  "invoice.amount": {
    "pt-AO": "Valor",
    "en": "Amount",
  },
  "invoice.filed_at": {
    "pt-AO": "Arquivada em",
    "en": "Filed at",
  },
  "invoice.sharepoint": {
    "pt-AO": "Localização no SharePoint",
    "en": "SharePoint location",
  },
  "invoice.planner_bucket": {
    "pt-AO": "Estado no Planner",
    "en": "Planner state",
  },
  "invoice.confidence": {
    "pt-AO": "Confiança da classificação",
    "en": "Classification confidence",
  },
  "invoice.method": {
    "pt-AO": "Método de classificação",
    "en": "Classification method",
  },
  "invoice.unset": {
    "pt-AO": "—",
    "en": "—",
  },
  "reasoning.heading": {
    "pt-AO": "Raciocínio do agente",
    "en": "Agent reasoning",
  },
  "reasoning.hidden": {
    "pt-AO":
      "O raciocínio está oculto para a sua organização por política de conformidade.",
    "en":
      "Reasoning is hidden for your organisation by compliance policy.",
  },
  "reasoning.empty": {
    "pt-AO":
      "Sem raciocínio registado (classificação automática por assinatura).",
    "en":
      "No reasoning recorded (deterministic, signature-based classification).",
  },
  // Collapsed-by-default summary label for the reasoning trace ({n} = step
  // count). The trace is AI-speak detail the persona rarely needs, so it
  // sits behind a disclosure (F37 §12.3 Medium). Two-form per the table's
  // `.one`/`.other` convention so a single-step trace reads "1 passo / 1 step"
  // (a single classification trace is reachable — n is not floored at 2).
  "reasoning.toggle.one": {
    "pt-AO": "Ver raciocínio do agente ({n} passo)",
    "en": "Show agent reasoning ({n} step)",
  },
  "reasoning.toggle.other": {
    "pt-AO": "Ver raciocínio do agente ({n} passos)",
    "en": "Show agent reasoning ({n} steps)",
  },
  "approvals.heading": {
    "pt-AO": "Aprovações",
    "en": "Approvals",
  },
  "approvals.tab.pending": {
    "pt-AO": "Pendentes",
    "en": "Pending",
  },
  "approvals.tab.recent": {
    "pt-AO": "Recentes",
    "en": "Recent",
  },
  "approvals.empty.pending": {
    "pt-AO": "Nada à espera de aprovação.",
    "en": "Nothing waiting for approval.",
  },
  "approvals.empty.recent": {
    "pt-AO": "Sem decisões recentes.",
    "en": "No recent decisions.",
  },
  "approvals.status.pending": {
    "pt-AO": "Pendente",
    "en": "Pending",
  },
  "approvals.status.approved": {
    "pt-AO": "Aprovada",
    "en": "Approved",
  },
  "approvals.status.aborted": {
    "pt-AO": "Cancelada",
    "en": "Aborted",
  },
  "approvals.waiting": {
    "pt-AO": "em espera",
    "en": "waiting",
  },
  "approvals.decided_by": {
    "pt-AO": "Decidido por",
    "en": "Decided by",
  },
  "exceptions.heading": {
    "pt-AO": "Excepções",
    "en": "Exceptions",
  },
  "exceptions.empty": {
    "pt-AO": "Sem excepções registadas.",
    "en": "No exceptions recorded.",
  },
  "exception.kind.low_confidence_classification": {
    "pt-AO": "Classificação de baixa confiança",
    "en": "Low-confidence classification",
  },
  "exception.kind.draft_validation_failed": {
    "pt-AO": "Falha na validação do rascunho",
    "en": "Draft validation failed",
  },
  "exception.kind.crescendo_suspected": {
    "pt-AO": "Suspeita de ataque incremental",
    "en": "Suspected crescendo attack",
  },
  "exception.kind.extraction_failed": {
    "pt-AO": "Falha na extracção do PDF",
    "en": "PDF extraction failed",
  },
  "exception.kind.outbound_blocked": {
    "pt-AO": "Envio bloqueado",
    "en": "Outbound blocked",
  },
  "exception.kind.unknown_sender": {
    "pt-AO": "Remetente desconhecido",
    "en": "Unknown sender",
  },
  "exception.kind.overdue_task_escalated": {
    "pt-AO": "Tarefa em atraso escalada",
    "en": "Overdue task escalated",
  },
  "exception.kind.unknown": {
    "pt-AO": "Excepção",
    "en": "Exception",
  },
  "cost.heading": {
    "pt-AO": "Custo de IA",
    "en": "AI cost",
  },
  "cost.total": {
    "pt-AO": "Total no período",
    "en": "Total for the period",
  },
  "cost.by_action": {
    "pt-AO": "Por tipo de acção",
    "en": "By action type",
  },
  // Cost by-action table column headers (A8 — the table had no <th>/<thead>).
  "cost.col.action": {
    "pt-AO": "Acção",
    "en": "Action",
  },
  "cost.col.amount": {
    "pt-AO": "Custo",
    "en": "Cost",
  },
  // Friendly operations-grade labels for the by-action cost rows. The
  // backend keys `by_action` by RAW registry action name (`classified_llm`,
  // `inbound_triaged`, …); rendering those verbatim leaked engine internals
  // onto the operator's screen (e.g. an alarming "memory_consolidation_failed"
  // line — an action that is in HIDDEN_ACTIONS and never surfaces elsewhere).
  // These map each cost-bearing action to a noun the persona recognises;
  // the whole `memory_consolidation_*` family collapses to "Manutenção do
  // agente" (one umbrella, aggregated in the panel) so internal maintenance
  // cost stays in the total — the customer's inference bill genuinely
  // includes it — without exposing a raw lifecycle verb. Unmapped keys fall
  // back to a humanised form (see `LlmCostPanel`), never the raw mono string.
  "cost.action.classified_llm": {
    "pt-AO": "Classificação de facturas",
    "en": "Invoice classification",
  },
  "cost.action.inbound_triaged": {
    "pt-AO": "Triagem de mensagens",
    "en": "Message triage",
  },
  "cost.action.inbound_disposition_classified": {
    "pt-AO": "Triagem de remetentes",
    "en": "Sender triage",
  },
  "cost.action.draft_generated": {
    "pt-AO": "Redacção de respostas",
    "en": "Reply drafting",
  },
  "cost.action.qa_session_completed": {
    "pt-AO": "Respostas a perguntas",
    "en": "Question answering",
  },
  "cost.action.maintenance": {
    "pt-AO": "Manutenção do agente",
    "en": "Agent maintenance",
  },
  "cost.advisory": {
    "pt-AO":
      "Valores indicativos — o seu plano é de avença com extracto mensal.",
    "en": "Advisory figures — your plan is flat-fee with a monthly statement.",
  },
  // Muted footnote (NOT an alert) — the success-path count of calls whose
  // cost couldn't be priced, surfaced for bill-defence transparency. Clarify
  // it's excluded from the total so the figure reconciles. Rendered in
  // inkSoft, never alert-red (an internal stamping-health concern is not a
  // customer alarm). F37 §12 P3 de-jargon pass.
  "cost.cost_errors": {
    "pt-AO": "Chamadas sem custo calculado (excluídas do total)",
    "en": "Calls with no cost computed (excluded from the total)",
  },
  "cost.hidden.title": {
    "pt-AO": "Plano de avença",
    "en": "Flat-fee plan",
  },
  // No trailing "Actividade no período:" — that dangled with nothing
  // visually tying it to the stat list below. The list now carries its own
  // `cost.activity.heading` label. Also de-jargoned "LLM" → "AI" (IA).
  "cost.hidden.body": {
    "pt-AO":
      "O seu plano é de avença, por isso o custo de IA não é discriminado.",
    "en":
      "Your plan is flat-fee, so itemised AI cost isn't surfaced.",
  },
  // Heading above the activity stat list (both the hidden narrative and the
  // shown breakdown's value line draw on the same two numbers).
  "cost.activity.heading": {
    "pt-AO": "Actividade no período",
    "en": "Activity for the period",
  },
  // Cost-vs-value line under the shown total — answers "what did this buy?".
  "cost.value_line": {
    "pt-AO": "{n} facturas processadas · ~{h}h poupadas",
    "en": "{n} invoices processed · ~{h}h saved",
  },
  "cost.activity.invoices": {
    "pt-AO": "facturas processadas",
    "en": "invoices processed",
  },
  "cost.activity.hours": {
    "pt-AO": "horas poupadas",
    "en": "hours saved",
  },
  "sovereignty.heading": {
    "pt-AO": "O que o agente pode fazer",
    "en": "What the agent can do",
  },
  "sovereignty.intro": {
    "pt-AO":
      "Os seus dados, na sua organização — o agente actua apenas nestas superfícies.",
    "en":
      "Your data, your organisation — the agent acts only on these surfaces.",
  },
  "sovereignty.col.capability": {
    "pt-AO": "Capacidade",
    "en": "Capability",
  },
  "sovereignty.col.status": {
    "pt-AO": "Estado",
    "en": "Status",
  },
  "sovereignty.col.last_activity": {
    "pt-AO": "Última actividade",
    "en": "Last activity",
  },
  // Range-aware activity-count column header. The {range} fragment is the
  // active range-picker label (e.g. "30 dias" / "Este ano"); the count is
  // computed over that same window, so the header can no longer mismatch it
  // (was hardcoded "Nos últimos 30 dias" — F37 §12.3 #4).
  "sovereignty.col.count": {
    "pt-AO": "Actividade ({range})",
    "en": "Activity ({range})",
  },
  "sovereignty.enabled": {
    "pt-AO": "Activada",
    "en": "Enabled",
  },
  "sovereignty.disabled": {
    "pt-AO": "Desactivada",
    "en": "Disabled",
  },
  "sovereignty.never": {
    "pt-AO": "Nunca usada",
    "en": "Never used",
  },
  "sovereignty.scope.heading": {
    "pt-AO": "Âmbito",
    "en": "Scope",
  },
  "sovereignty.scope.intro": {
    "pt-AO": "Limitado a:",
    "en": "Limited to:",
  },
  "sovereignty.empty": {
    "pt-AO": "Nenhuma capacidade activada para a sua organização.",
    "en": "No capabilities enabled for your organisation.",
  },
  "sovereignty.axis.mail.send": {
    "pt-AO": "Enviar e-mail",
    "en": "Send email",
  },
  "sovereignty.axis.sharepoint.write": {
    "pt-AO": "Arquivar documentos",
    "en": "File documents",
  },
  "sovereignty.axis.planner.create_task": {
    "pt-AO": "Criar tarefas",
    "en": "Create tasks",
  },
  "sovereignty.axis.planner.move_bucket": {
    "pt-AO": "Mover tarefas",
    "en": "Move tasks",
  },
  "sovereignty.axis.teams.channel_post": {
    "pt-AO": "Publicar no Teams",
    "en": "Post to Teams",
  },
  "sovereignty.axis.llm.drafter": {
    "pt-AO": "Redigir respostas",
    "en": "Draft replies",
  },
  "sovereignty.axis.llm.triage": {
    "pt-AO": "Triagem de mensagens",
    "en": "Triage messages",
  },
  "sovereignty.axis.llm.qa_loop": {
    "pt-AO": "Responder a perguntas",
    "en": "Answer questions",
  },
  "sovereignty.scope.sharepoint.sites": {
    "pt-AO": "sites do SharePoint",
    "en": "SharePoint sites",
  },
  "sovereignty.scope.sharepoint.libraries": {
    "pt-AO": "bibliotecas",
    "en": "libraries",
  },
  "sovereignty.scope.planner.plans": {
    "pt-AO": "planos",
    "en": "plans",
  },
  "sovereignty.scope.teams.teams": {
    "pt-AO": "equipas",
    "en": "teams",
  },
  "sovereignty.scope.teams.channels": {
    "pt-AO": "canais",
    "en": "channels",
  },
  "sovereignty.scope.mail.mailboxes": {
    "pt-AO": "caixas de correio",
    "en": "mailboxes",
  },
  "sovereignty.scope.mail.excluded_folders": {
    "pt-AO": "pastas excluídas",
    "en": "excluded folders",
  },

  // --- F37 UX redesign: tabs ---------------------------------------------
  "tab.overview": {
    "pt-AO": "Resumo",
    "en": "Overview",
  },
  "tab.invoices": {
    "pt-AO": "Facturas",
    "en": "Invoices",
  },
  "tab.atividade": {
    "pt-AO": "Actividade",
    "en": "Activity",
  },
  "tab.trust": {
    "pt-AO": "Confiança e custo",
    "en": "Trust & cost",
  },

  // --- F37 §12.6 capability summary strip (Task 7) ----------------------
  "capsum.summary": {
    "pt-AO": "Activo em {x} de {y} capacidades",
    "en": "Active on {x} of {y} capabilities",
  },
  "capsum.scope": {
    "pt-AO": "âmbito: {scope}",
    "en": "scope: {scope}",
  },
  "capsum.link": {
    "pt-AO": "Ver tudo",
    "en": "View all",
  },
  // "Activo em 7 de 8 capacidades" is a PERMISSION count, and it rendered
  // identically on a window where the agent did nothing at all (design
  // review finding 10). When the window carries zero activity, say that
  // instead of implying activity behind the number.
  "capsum.idle": {
    "pt-AO": "Autorizado em {x} de {y} capacidades · sem actividade nesta janela",
    "en": "Authorised on {x} of {y} capabilities · no activity in this window",
  },

  // --- F37 §12 redesign: "Precisa de si" action queue (Task 3) ----------
  "needs.title": {
    "pt-AO": "Precisa de si",
    "en": "Needs you",
  },
  "needs.eyebrow": {
    "pt-AO": "Acção necessária",
    "en": "Action needed",
  },
  "needs.count.one": {
    "pt-AO": "{n} item",
    "en": "{n} item",
  },
  "needs.count.other": {
    "pt-AO": "{n} itens",
    "en": "{n} items",
  },
  "needs.cta.approval": {
    "pt-AO": "Ver aprovação",
    "en": "View approval",
  },
  "needs.cta.review": {
    "pt-AO": "Rever factura",
    "en": "Review invoice",
  },
  // {ago} carries its own "há …" / "… ago" framing from relativeTime.
  "needs.age.waiting": {
    "pt-AO": "em espera {ago}",
    "en": "waiting {ago}",
  },
  // Footer strip: what the card covers, and where the decision actually
  // happens. Replaces the "Marcar como tratado" button, which hid the row
  // in local state while the approval stayed pending server-side — so it
  // came back on the next refresh (design review finding 4). The dashboard
  // shows; it does not act.
  "needs.scope_note": {
    "pt-AO":
      "Tudo o que está pendente, seja de que período for. As decisões acontecem no cartão de aprovação do Teams.",
    "en":
      "Everything still pending, whatever the period. Decisions happen on the Teams approval card.",
  },
  // Fallback title for a pending approval with no subject on the audit row.
  "needs.approval.title": {
    "pt-AO": "Aprovação pendente",
    "en": "Pending approval",
  },
  "needs.ok.title": {
    "pt-AO": "Tudo tratado",
    "en": "All handled",
  },
  // {ago} = time of the last dashboard refresh ("última verificação …").
  "needs.ok.body": {
    "pt-AO":
      "Nada precisa da sua atenção. O Kwanza continua a vigiar — última verificação {ago}.",
    "en":
      "Nothing needs your attention. Kwanza keeps watching — last checked {ago}.",
  },

  // --- F37 §13 Atividade tab --------------------------------------------
  // The seven curated `Tipo` filter labels. These keys are the SPA side of
  // the canonical `ACTIVITY_TYPE_KEYS` map in
  // `kwanza.dashboard.render.activity_types`; the backend parity test
  // (`tests/test_dashboard_activity_i18n_parity.py`) asserts every type key
  // has a label here. The SAME label renders both the filter chip AND each
  // row's type tag — operations-grade nouns, never the raw registry verbs
  // (locked constraint ④).
  "tipo.faturas": {
    "pt-AO": "Facturas",
    "en": "Invoices",
  },
  "tipo.comunicacao": {
    "pt-AO": "Comunicação",
    "en": "Communication",
  },
  "tipo.aprovacoes": {
    "pt-AO": "Aprovações",
    "en": "Approvals",
  },
  "tipo.tarefas": {
    "pt-AO": "Tarefas",
    "en": "Tasks",
  },
  "tipo.correcoes": {
    "pt-AO": "Correções",
    "en": "Corrections",
  },
  "tipo.seguranca": {
    "pt-AO": "Segurança",
    "en": "Security",
  },
  "tipo.aprendizagem": {
    "pt-AO": "Aprendizagem",
    "en": "Learning",
  },

  // The three `Estado` tri-state values (Tudo / Concluídas / A precisar de
  // atenção → all / done / attention). Paired with the backend route's
  // enumerated `estado` param; the parity test pins all three.
  "estado.all": {
    "pt-AO": "Tudo",
    "en": "All",
  },
  "estado.done": {
    "pt-AO": "Concluídas",
    "en": "Completed",
  },
  "estado.attention": {
    "pt-AO": "A precisar de atenção",
    "en": "Needs attention",
  },

  // Atividade-tab chrome.
  "atividade.feed_label": {
    "pt-AO": "Registo de actividade",
    "en": "Activity log",
  },
  "atividade.tipo_label": {
    "pt-AO": "Tipo",
    "en": "Type",
  },
  "atividade.estado_label": {
    "pt-AO": "Estado",
    "en": "Status",
  },
  "atividade.load_more": {
    "pt-AO": "Carregar mais",
    "en": "Load more",
  },
  "atividade.loading_more": {
    "pt-AO": "A carregar mais…",
    "en": "Loading more…",
  },
  "atividade.empty": {
    "pt-AO": "Sem actividade nesta janela.",
    "en": "No activity in this window.",
  },
  // P24-safe loaded-count label — "eventos", never an outcome-blind "feito"
  // ("things done"). Two-form per the `.one`/`.other` convention.
  "atividade.count.one": {
    "pt-AO": "{n} evento",
    "en": "{n} event",
  },
  "atividade.count.other": {
    "pt-AO": "{n} eventos",
    "en": "{n} events",
  },

  // --- status strip chrome ----------------------------------------------
  "app.product_name": {
    "pt-AO": "Copiloto de facturação",
    "en": "Invoice copilot",
  },
  // Boundary line under the product name in the status strip — reinforces
  // the sovereignty story ("the agent acts ONLY here"). {channel} is the
  // live Teams channel name (getContext().channelName) / tenant config — a
  // Variables-layer value, never baked into the bundle.
  "status.scope_line": {
    "pt-AO": "a actuar apenas no canal {channel}",
    "en": "acting only in the {channel} channel",
  },
  // Support-info popover (ⓘ) — version + last heartbeat tucked out of the
  // chrome (replaces the always-on version chip + the Diagnostics tab's
  // VersionDetail).
  "status.support_info": {
    "pt-AO": "Informação de suporte",
    "en": "Support info",
  },
  "status.reload": {
    "pt-AO": "Actualizar",
    "en": "Refresh",
  },
  "status.reloading": {
    "pt-AO": "A actualizar…",
    "en": "Refreshing…",
  },
  "status.version_label": {
    "pt-AO": "Versão",
    "en": "Version",
  },
  // "Updated {ago}" — the {ago} fragment carries its own "ago"/"há"
  // framing from relativeTime, so the prefix is just the verb.
  "status.updated_prefix": {
    "pt-AO": "Actualizado",
    "en": "Updated",
  },
  "status.updated_never": {
    "pt-AO": "Ainda não actualizado",
    "en": "Not updated yet",
  },

  // --- health phrasings ({ago} = relativeTime "ago" fragment) ------------
  "health.running.active": {
    "pt-AO": "O Kwanza está a funcionar — última actividade {ago}.",
    "en": "Kwanza is running — last active {ago}.",
  },
  "health.running.quiet": {
    "pt-AO": "O Kwanza está a funcionar. Sem actividade nova de momento.",
    "en": "Kwanza is running. No new activity right now.",
  },
  "health.idle": {
    "pt-AO": "O Kwanza foi visto pela última vez {ago}.",
    "en": "Kwanza was last seen {ago}.",
  },
  "health.stale": {
    "pt-AO":
      "O Kwanza parece estar parado — visto pela última vez {ago}. Contacte o suporte.",
    "en": "Kwanza appears to be down — last seen {ago}. Contact support.",
  },
  "health.unknown": {
    "pt-AO": "Ainda sem dados do Kwanza para a sua organização.",
    "en": "No Kwanza data yet for your organisation.",
  },

  // --- overview summary line ({n} invoices, {h} hours) -------------------
  "overview.summary": {
    "pt-AO": "Este período: {n} facturas processadas, ~{h}h poupadas.",
    "en": "This period: {n} invoices processed, ~{h}h saved.",
  },

  // --- relative time ("ago"-style fragments; {n} count) ------------------
  "time.just_now": {
    "pt-AO": "agora mesmo",
    "en": "just now",
  },
  "time.minutes.one": {
    "pt-AO": "há {n} minuto",
    "en": "{n} minute ago",
  },
  "time.minutes.other": {
    "pt-AO": "há {n} minutos",
    "en": "{n} minutes ago",
  },
  "time.hours.one": {
    "pt-AO": "há {n} hora",
    "en": "{n} hour ago",
  },
  "time.hours.other": {
    "pt-AO": "há {n} horas",
    "en": "{n} hours ago",
  },
  "time.days.one": {
    "pt-AO": "há {n} dia",
    "en": "{n} day ago",
  },
  "time.days.other": {
    "pt-AO": "há {n} dias",
    "en": "{n} days ago",
  },

  // --- time-range picker -------------------------------------------------
  "range.heading": {
    "pt-AO": "Período",
    "en": "Period",
  },
  "range.today": {
    "pt-AO": "Hoje",
    "en": "Today",
  },
  "range.7d": {
    "pt-AO": "7 dias",
    "en": "7 days",
  },
  "range.30d": {
    "pt-AO": "30 dias",
    "en": "30 days",
  },
  "range.90d": {
    "pt-AO": "90 dias",
    "en": "90 days",
  },
  "range.year": {
    "pt-AO": "Este ano",
    "en": "This year",
  },
  "range.all": {
    "pt-AO": "Desde sempre",
    "en": "All time",
  },

  // --- diagnostics: version detail ---------------------------------------
  "version.heading": {
    "pt-AO": "Versão e estado do serviço",
    "en": "Version & service status",
  },
  "version.running_label": {
    "pt-AO": "Versão em execução",
    "en": "Running version",
  },
  "version.last_heartbeat": {
    "pt-AO": "Último sinal de vida",
    "en": "Last heartbeat",
  },
  "version.last_activity": {
    "pt-AO": "Última actividade",
    "en": "Last activity",
  },
} as const;

export type StringKey = keyof typeof STRINGS;

/** Resolve a locale (which may be a regional variant like `en-US`)
 * onto one of `SUPPORTED_LANGUAGES`. Mirrors the backend's
 * `_resolve_language()` shape so behaviour is identical across surfaces.
 */
export function resolveLanguage(input: string | null | undefined): Language {
  if (!input) return DEFAULT_LANGUAGE;
  if ((SUPPORTED_LANGUAGES as readonly string[]).includes(input)) {
    return input as Language;
  }
  const base = input.split("-", 1)[0]?.toLowerCase();
  if (!base) return DEFAULT_LANGUAGE;
  for (const lang of SUPPORTED_LANGUAGES) {
    if (lang.toLowerCase().startsWith(base)) {
      return lang;
    }
  }
  return DEFAULT_LANGUAGE;
}

/** Look up `key` in `language`, falling back to the default language
 * if the entry is missing for the requested locale. Throws in dev
 * mode when `key` is unknown — that's a programmer bug, not a runtime
 * condition (the parity test should have caught it).
 */
export function t(key: StringKey, language: Language = DEFAULT_LANGUAGE): string {
  const entry = STRINGS[key];
  if (!entry) {
    throw new Error(`Unknown string key: ${String(key)}`);
  }
  return entry[language] ?? entry[DEFAULT_LANGUAGE];
}

/** Like {@link t}, but interpolates `{name}` placeholders from `vars`.
 *
 * `t()` has no interpolation by design; `tn()` adds the minimal
 * `{name}` → value substitution the F37 status strip needs (`{ago}`,
 * `{n}`, `{h}`). Unknown placeholders are left intact (so a missing var
 * is visible in dev rather than silently blanked); extra vars are
 * ignored. Values are coerced to strings.
 */
export function tn(
  key: StringKey,
  vars: Record<string, string | number>,
  language: Language = DEFAULT_LANGUAGE,
): string {
  return t(key, language).replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

/** Programmatic access to the underlying table — used only by the
 * parity test. Not part of the public surface; do not import this from
 * components.
 */
export function _stringsForTest(): typeof STRINGS {
  return STRINGS;
}
